import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import {
  xdrServiceOptions,
  applianceModelOptions,
  virtualOSOptions,
  stateOptions,
  xdrServiceOptionsForDropdown,
  getXdrDeliveryOptions,
  getAllXdrDeliveryOptions,
} from '../../../mock-data/services-mock-data';
import { MxdrFormData } from 'src/app/core/models/customer';

export interface MxdrForm {
  selectedService: FormControl<string | null>;
  govCloudRequired: FormControl<boolean | null>;
  extendedLogRetention: FormControl<boolean | null>;
  adUsers: FormControl<number | null>;
  servers: FormControl<number | null>;
  xdrServiceOption: FormControl<any | null>;
  appliances: FormControl<number | null>;
  applianceModels: FormControl<any[] | null>;
  virtualOS: FormControl<any | null>;
  totalMachines: FormControl<number | null>;
  totalDailyIngestion: FormControl<number | null>;
  cmmcExtendedLogRetention: FormControl<boolean | null>;
}

@Injectable({
  providedIn: 'root',
})
export class MxdrFormService {
  constructor(private fb: FormBuilder) {}

  createMxdrForm(data?: MxdrFormData): FormGroup<MxdrForm> {
    const form = this.fb.group<MxdrForm>({
      selectedService: this.fb.control<string | null>(data?.selectedService || null, [Validators.required]),
      govCloudRequired: this.fb.control<boolean | null>(data?.govCloudRequired || false),
      extendedLogRetention: this.fb.control<boolean | null>(data?.extendedLogRetention || false),
      adUsers: this.fb.control<number | null>(data?.adUsers || 0, [Validators.min(0)]),
      servers: this.fb.control<number | null>(data?.servers || 0, [Validators.min(0)]),
      xdrServiceOption: this.fb.control<any | null>(data?.xdrServiceOption || null),
      appliances: this.fb.control<number | null>(data?.appliances || 0, [Validators.min(0)]),
      applianceModels: this.fb.control<any[] | null>(data?.applianceModels || []),
      virtualOS: this.fb.control<any | null>(data?.virtualOS || null),
      totalMachines: this.fb.control<number | null>(data?.totalMachines || 0, [Validators.min(0)]),
      totalDailyIngestion: this.fb.control<number | null>(data?.totalDailyIngestion || 0, [Validators.min(0)]),
      cmmcExtendedLogRetention: this.fb.control<boolean | null>(data?.cmmcExtendedLogRetention || false),
    });

    if (data) {
      this.patchFormWithData(form, data);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<MxdrForm>, data: MxdrFormData): void {
    form.patchValue({
      selectedService: data.selectedService || null,
      govCloudRequired: data.govCloudRequired || false,
      extendedLogRetention: data.extendedLogRetention || false,
      adUsers: data.adUsers || 0,
      servers: data.servers || 0,
      xdrServiceOption: data.xdrServiceOption || null,
      appliances: data.appliances || 0,
      applianceModels: data.applianceModels || [],
      virtualOS: data.virtualOS || null,
      totalMachines: data.totalMachines || 0,
      totalDailyIngestion: data.totalDailyIngestion || 0,
      cmmcExtendedLogRetention: data.cmmcExtendedLogRetention || false,
    });

    // Apply conditional validation if service is already selected
    if (data.selectedService) {
      this.applyConditionalValidation(form, data.selectedService);
    }
  }

  selectService(form: FormGroup<MxdrForm>, serviceValue: string): void {
    form.patchValue({ selectedService: serviceValue });
    this.resetServiceSpecificFields(form, serviceValue);
    this.applyConditionalValidation(form, serviceValue);

    // Reset XDR service option when service changes since delivery options might be different
    form.patchValue({ xdrServiceOption: null });
  }

  private resetServiceSpecificFields(form: FormGroup<MxdrForm>, serviceValue: string): void {
    if (serviceValue === 'xdr-standard' || serviceValue === 'xdr-govcloud') {
      // Reset CMMC fields
      form.patchValue({
        totalMachines: 0,
        totalDailyIngestion: 0,
        cmmcExtendedLogRetention: false,
      });
    } else if (serviceValue === 'xdr-cmmc') {
      // Reset Standard/GovCloud fields
      form.patchValue({
        adUsers: 0,
        servers: 0,
        xdrServiceOption: null,
        appliances: 0,
        applianceModels: [],
        virtualOS: null,
        govCloudRequired: false,
        extendedLogRetention: false,
      });
    }
  }

  private applyConditionalValidation(form: FormGroup<MxdrForm>, serviceValue: string): void {
    if (serviceValue === 'xdr-standard' || serviceValue === 'xdr-govcloud') {
      // Add validation for Standard/GovCloud specific fields - only the required ones
      form.get('adUsers')?.setValidators([Validators.required, Validators.min(1)]);
      form.get('servers')?.setValidators([Validators.required, Validators.min(1)]);
      form.get('xdrServiceOption')?.setValidators([Validators.required]);
      form.get('appliances')?.clearValidators();
      form.get('applianceModels')?.clearValidators();
      form.get('virtualOS')?.clearValidators();
    } else if (serviceValue === 'xdr-cmmc') {
      // Add validation for CMMC specific fields
      form.get('totalMachines')?.setValidators([Validators.required, Validators.min(1)]);
      form.get('totalDailyIngestion')?.setValidators([Validators.required, Validators.min(1)]);

      // Remove validation from Standard/GovCloud fields
      form.get('adUsers')?.clearValidators();
      form.get('servers')?.clearValidators();
      form.get('xdrServiceOption')?.clearValidators();
    } else {
      // No service selected - clear all conditional validators
      form.get('adUsers')?.clearValidators();
      form.get('servers')?.clearValidators();
      form.get('xdrServiceOption')?.clearValidators();
      form.get('appliances')?.clearValidators();
      form.get('applianceModels')?.clearValidators();
      form.get('virtualOS')?.clearValidators();
      form.get('totalMachines')?.clearValidators();
      form.get('totalDailyIngestion')?.clearValidators();
    }

    // Update validation for all affected controls
    form.get('adUsers')?.updateValueAndValidity();
    form.get('servers')?.updateValueAndValidity();
    form.get('xdrServiceOption')?.updateValueAndValidity();
    form.get('appliances')?.updateValueAndValidity();
    form.get('applianceModels')?.updateValueAndValidity();
    form.get('virtualOS')?.updateValueAndValidity();
    form.get('totalMachines')?.updateValueAndValidity();
    form.get('totalDailyIngestion')?.updateValueAndValidity();
  }

  getFormData(form: FormGroup<MxdrForm>): MxdrFormData {
    const value = form.value;
    return {
      selectedService: value.selectedService || null,
      govCloudRequired: value.govCloudRequired || false,
      extendedLogRetention: value.extendedLogRetention || false,
      adUsers: value.adUsers || 0,
      servers: value.servers || 0,
      xdrServiceOption: this.getDropdownValue(value.xdrServiceOption),
      appliances: value.appliances || 0,
      applianceModels: value.applianceModels || [],
      virtualOS: this.getDropdownValue(value.virtualOS),
      totalMachines: value.totalMachines || 0,
      totalDailyIngestion: value.totalDailyIngestion || 0,
      cmmcExtendedLogRetention: value.cmmcExtendedLogRetention || false,
    };
  }

  onTotalMachinesChange(form: FormGroup<MxdrForm>, value: number | null): void {
    form.patchValue({ totalMachines: value || 0 });
  }

  onTotalDailyIngestionChange(form: FormGroup<MxdrForm>, value: number | null): void {
    form.patchValue({ totalDailyIngestion: value || 0 });
  }

  getDropdownValue(value: any): string {
    return value?.value || value || '';
  }

  findDropdownObject(options: any[], value: string): any {
    return options.find((option) => option.value === value);
  }

  getMultiselectValues(values: any[]): string[] {
    return values?.map((v) => v.value || v) || [];
  }

  findMultiselectObjects(options: any[], values: string[]): any[] {
    return options.filter((option) => values.includes(option.value));
  }

  getServiceOptions() {
    return xdrServiceOptionsForDropdown;
  }

  getXdrServiceDeliveryOptions() {
    return getAllXdrDeliveryOptions();
  }

  getXdrServiceDeliveryOptionsForService(serviceName: string) {
    return getXdrDeliveryOptions(serviceName);
  }

  getServiceDescription(serviceValue: string): string {
    const service = this.getServiceOptions().find((s: any) => s.value === serviceValue);
    return service?.description || '';
  }

  getDeliveryOptionDescription(serviceName: string, deliveryValue: string): string {
    const deliveryOptions = getXdrDeliveryOptions(serviceName);
    const option = deliveryOptions.find((opt: any) => opt.value === deliveryValue);
    return option?.description || '';
  }

  getApplianceModelOptions() {
    return applianceModelOptions;
  }

  getVirtualOSOptions() {
    return virtualOSOptions;
  }

  getStateOptions() {
    return stateOptions;
  }

  debugFormState(form: FormGroup<MxdrForm>): void {
    console.log('=== MXDR Form Debug ===');
    console.log('Form Valid:', form.valid);
    console.log('Form Invalid:', form.invalid);
    console.log('Form Pending:', form.pending);
    console.log('Form Dirty:', form.dirty);
    console.log('Form Touched:', form.touched);
    console.log('Form Errors:', form.errors);
    console.log('Selected Service:', form.get('selectedService')?.value);
    console.log('Selected Service Valid:', form.get('selectedService')?.valid);
    console.log('Selected Service Errors:', form.get('selectedService')?.errors);
    console.log('XDR Service Option:', form.get('xdrServiceOption')?.value);
    console.log('XDR Service Option Valid:', form.get('xdrServiceOption')?.valid);
    console.log('XDR Service Option Errors:', form.get('xdrServiceOption')?.errors);
    console.log('AD Users:', form.get('adUsers')?.value);
    console.log('AD Users Valid:', form.get('adUsers')?.valid);
    console.log('AD Users Errors:', form.get('adUsers')?.errors);
    console.log('Servers:', form.get('servers')?.value);
    console.log('Servers Valid:', form.get('servers')?.valid);
    console.log('Servers Errors:', form.get('servers')?.errors);
    console.log('Appliances:', form.get('appliances')?.value);
    console.log('Appliances Valid:', form.get('appliances')?.valid);
    console.log('Appliances Errors:', form.get('appliances')?.errors);
    console.log('Total Machines:', form.get('totalMachines')?.value);
    console.log('Total Machines Valid:', form.get('totalMachines')?.valid);
    console.log('Total Machines Errors:', form.get('totalMachines')?.errors);
    console.log('Total Daily Ingestion:', form.get('totalDailyIngestion')?.value);
    console.log('Total Daily Ingestion Valid:', form.get('totalDailyIngestion')?.valid);
    console.log('Total Daily Ingestion Errors:', form.get('totalDailyIngestion')?.errors);

    // Log all form controls
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
      });
    });
  }
}
